import { Controller, useForm, useFormState } from 'react-hook-form';
import { PortfolioCompany, CompanyDetails } from '../../models/types';
import { VStack } from '@chakra-ui/react';
import { FormField, Input, NumberInput, Tooltip } from 'Atoms';
import { CreatableSelect, Modal, RadioCardGroup, RadioCardProps } from 'Molecules';
import { useUserCompaniesQuery } from 'models';
import { useAuth } from 'Providers/auth/AuthProvider.tsx';
import { components } from 'react-select';
import { useState, useEffect, useMemo } from 'react';
import { useCurrentCompany } from 'utils/hooks';
import { QuarterEnum_Enum_, usePortfoliosQuery } from '../../models/__generated__/graphql';
import { useTranslation } from 'utils/translation';
import { useUpsertPortfolioCompany } from 'containers/Portfolios';
import { useToast } from '../../utils/hooks/useToast';
import { useParams } from 'react-router-dom';
import { splitQuarterYear } from 'utils/date';

export enum EligibilityStatus {
  notSure = 'NOT_SURE',
  notEligible = 'NOT_ELIGIBLE',
  eligible = 'ELIGIBLE',
}

type EligibilityOption = Omit<RadioCardProps, 'isSelected'>;

type PortfolioFields = {
  amount: number;
  company: {
    id?: string;
    name: string;
    isEstimate?: boolean;
    isGroupOwner: boolean;
    isPortfolioOwner: boolean;
  };
  contactPersonName: string;
  contactPersonEmail: string;
  marketValue?: number;
  eligibilityStatus: EligibilityStatus;
};

export const UserCompanySelect = ({
  value,
  setValue,
  userCompanies,
  currentPortfolioCompanyIds,
}: {
  value: PortfolioFields['company'] | undefined;
  setValue: (company: PortfolioFields['company'] | undefined) => void;
  userCompanies: CompanyDetails[];
  currentPortfolioCompanyIds: string[];
}) => {
  const { reportingYear = '' } = useParams();
  const [quarter] = splitQuarterYear(reportingYear);

  const { t } = useTranslation('portfolio');

  const userCompaniesFiltered = useMemo(
    () => userCompanies.filter((c) => !currentPortfolioCompanyIds.includes(c.id)),
    [userCompanies, currentPortfolioCompanyIds]
  );

  return (
    <CreatableSelect
      value={value}
      components={{
        Option: (props) => {
          return (
            <components.Option {...props}>
              {props.isDisabled ? (
                <Tooltip label={t('portfolio:notAbleToAddInvestor')} placement="right-start">
                  {props.label}
                </Tooltip>
              ) : (
                props.label
              )}
            </components.Option>
          );
        },
      }}
      isClearable={true}
      options={[
        {
          label: t('portfolio:search.yourCompanies'),
          options: userCompaniesFiltered.map((c) => ({ ...c, isEstimate: false })),
        },
      ]}
      createOptionPosition="last"
      placeholder={t('portfolio:fields.company.placeholder')}
      getOptionValue={(c) => c.id ?? ''}
      getOptionLabel={(c) => c.name}
      getNewOptionData={(_input, label) => ({
        id: undefined,
        name: label as string,
        industries: [],
        isEstimate: false,
        isGroupOwner: false,
        isPortfolioOwner: false,
      })}
      isOptionDisabled={(c) => (c.isGroupOwner || c.isPortfolioOwner) && quarter !== 'Q1'}
      isValidNewOption={(input) =>
        userCompanies.find((c) => c.name.toUpperCase() === input.toUpperCase()) === undefined
      }
      onChange={(company) => {
        setValue(company ?? undefined);
      }}
      formatCreateLabel={(input) => t('portfolio:createNew', { name: input })}
    />
  );
};

export const PortfolioCompanyEditorModal = ({
  portfolioCompany,
  isOpen,
  onClose,
  reportYear,
  currentPortfolioCompanyIds,
}: {
  portfolioCompany?: PortfolioCompany;
  isOpen: boolean;
  onClose: () => void;
  reportYear?: string;
  currentPortfolioCompanyIds: string[];
}) => {
  const { t } = useTranslation(['portfolio', 'common']);
  const { portfolioId, companyId } = useParams();
  const toast = useToast();
  const { user } = useAuth();
  const upsertPortfolioCompany = useUpsertPortfolioCompany();
  const { company: currentCompany } = useCurrentCompany();
  const [loading, setLoading] = useState(false);
  const initialValueOfInvestment = useMemo(() => {
    if (!portfolioCompany?.valueOfInvestments) return 0;
    const { q1 = 0, q2 = 0, q3 = 0, q4 = 0 } = portfolioCompany?.valueOfInvestments;
    return (q1 + q2 + q3 + q4) / 4;
  }, [portfolioCompany]);

  const { register, control, handleSubmit, reset, getValues } = useForm<PortfolioFields>({
    mode: 'all',
    reValidateMode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      amount: initialValueOfInvestment ?? undefined,
      contactPersonName: portfolioCompany?.contactPersonName ?? undefined,
      contactPersonEmail: portfolioCompany?.contactPersonEmail ?? undefined,
      marketValue: portfolioCompany?.marketValue ?? undefined,
      eligibilityStatus:
        (portfolioCompany?.eligibilityStatus as EligibilityStatus) ?? EligibilityStatus.notSure,
    },
  });

  const [quarter, year] = splitQuarterYear(reportYear);

  const { data } = useUserCompaniesQuery({
    variables: {
      id: user?.id,
    },
    skip: !user?.id,
  });

  const { data: companiesInPortfolio } = usePortfoliosQuery({
    variables: {
      companyId: companyId ?? '',
    },
  });

  const userCompanies: CompanyDetails[] =
    data?.data?.companies?.map(({ company }) => company) ?? [];

  const currentPortfolioCompanies =
    companiesInPortfolio?.portfolios?.reduce(
      (acc, portfolio) => [
        ...acc,
        ...portfolio.portfolioCompanies
          ?.filter(
            (portCo) =>
              !!portCo.company && !userCompanies.find((userCo) => userCo.id === portCo.company?.id)
          )
          .map((portCo) => portCo?.company),
      ],
      [] as any[]
    ) ?? [];

  const combinedCompanies = [
    ...new Set([...userCompanies, ...currentPortfolioCompanies]),
  ] as CompanyDetails[];

  const eligibilityOptions: EligibilityOption[] = [
    {
      title: t('portfolio:eligibilityOptions.notSure.title'),
      value: EligibilityStatus.notSure,
      subtitle: t('portfolio:eligibilityOptions.notSure.subTitle'),
    },
    {
      title: t('portfolio:eligibilityOptions.eligible.title'),
      value: EligibilityStatus.eligible,
      subtitle: t('portfolio:eligibilityOptions.eligible.subTitle'),
    },
    {
      title: t('portfolio:eligibilityOptions.nonEligible.title'),
      value: EligibilityStatus.notEligible,
      subtitle: t('portfolio:eligibilityOptions.nonEligible.subTitle'),
    },
  ];

  useEffect(() => {
    if (portfolioCompany) {
      reset({
        amount: initialValueOfInvestment ?? undefined,
        company: {
          id: portfolioCompany?.company?.id,
          name: portfolioCompany?.company?.name,
          isEstimate: !!portfolioCompany?.estimateCompany,
        },
        contactPersonName: portfolioCompany?.contactPersonName ?? undefined,
        contactPersonEmail: portfolioCompany?.contactPersonEmail ?? undefined,
        marketValue: portfolioCompany?.marketValue ?? undefined,
        eligibilityStatus: portfolioCompany?.eligibilityStatus as EligibilityStatus,
      });
    }
  }, [portfolioCompany]);
  const { isValid, isDirty, errors } = useFormState({ control });

  const handleSave = async (editedPortfolioCompany: PortfolioFields) => {
    setLoading(true);
    const { company } = editedPortfolioCompany;
    const c = {
      company: {
        id: company?.id,
        name: company?.name,
        currency: currentCompany?.currency ?? 'NOK',
        industries: [],
      },
    };

    const newPc = await upsertPortfolioCompany({
      ...c,
      amount: editedPortfolioCompany.amount,
      marketValue: editedPortfolioCompany.marketValue,
      contactPersonName: editedPortfolioCompany.contactPersonName,
      contactPersonEmail: editedPortfolioCompany.contactPersonEmail,
      portfolioId: portfolioId ?? '',
      eligibilityStatus: editedPortfolioCompany.eligibilityStatus,
      year: Number(year),
      quarter: (quarter as QuarterEnum_Enum_) ?? 'Q1',
      createFirstBusinessUnit: false,
    });

    if (newPc)
      toast({
        text: currentCompany
          ? t('portfolio:toast.companyUpdated', {
              companyName: newPc.company?.name ?? newPc.estimateCompany?.name,
            })
          : t('portfolio:toast.companyCreated', {
              companyName: newPc.company?.name ?? newPc.estimateCompany?.name,
            }),
      });
    reset({
      amount: undefined,
      company: undefined,
      contactPersonName: undefined,
      contactPersonEmail: undefined,
      marketValue: undefined,
      eligibilityStatus: EligibilityStatus.notSure,
    });
    setLoading(false);
    onClose();
  };
  return (
    <Modal
      title={
        !portfolioCompany && !reportYear
          ? `${t('portfolio:addAsset')}`
          : !portfolioCompany
            ? `${t('portfolio:addAsset')} ${t('portfolio:periodAsset')}
             ${reportYear}`
            : `${t('portfolio:editAsset')} ${t('portfolio:periodAsset')}
             ${reportYear}`
      }
      isOpen={isOpen}
      onClose={onClose}
      confirmText={!portfolioCompany ? t('portfolio:addAsset') : t('portfolio:updateAsset')}
      confirmButtonProps={{
        type: 'submit',
        form: 'portfolio-company-editor-form',
        isDisabled: !isValid || !isDirty || loading,
      }}
    >
      <form onSubmit={handleSubmit(handleSave)} id="portfolio-company-editor-form">
        <VStack spacing="md">
          {!portfolioCompany && (
            <Controller
              name="company"
              control={control}
              rules={{ required: t('portfolio:required.name') }}
              render={({ field: { onChange, value } }) => (
                <FormField
                  label={t('common:fields.companyName.label')}
                  id="name"
                  isRequired
                  isInvalid={!!errors.company}
                  error={t('portfolio:required.name')}
                >
                  <UserCompanySelect
                    value={value}
                    setValue={(company) => company && onChange(company)}
                    userCompanies={combinedCompanies}
                    currentPortfolioCompanyIds={currentPortfolioCompanyIds}
                  />
                </FormField>
              )}
            />
          )}

          {!portfolioCompany && (
            <Controller
              name="marketValue"
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormField
                  label={t('portfolio:fields.marketValue.label')}
                  id="amount"
                  isInvalid={!!errors.marketValue}
                  error={errors.marketValue?.message}
                  helpText={t('portfolio:fields.marketValue.tooltip')}
                >
                  <NumberInput
                    unit={currentCompany?.currency}
                    value={value}
                    onChange={(v) => onChange(Number(v))}
                    min={0}
                    max={Number.MAX_VALUE}
                    keepWithinRange
                    clampValueOnBlur
                    replaceNanWithZero
                  />
                </FormField>
              )}
            />
          )}
          {!portfolioCompany && (
            <Controller
              name="amount"
              control={control}
              rules={{
                required: t('portfolio:required.allocation'),
                validate: {
                  allocationValidation: (value) =>
                    value <= (getValues('marketValue') ?? 0) || t('portfolio:allocationValidation'),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <FormField
                  label={t('portfolio:fields.amount.label')}
                  id="amount"
                  isRequired
                  isInvalid={!!errors.amount}
                  error={errors.amount?.message}
                  helpText={
                    'This value will be set for all quarters. You can later edit each quarter individually.'
                  }
                >
                  <NumberInput
                    unit={currentCompany?.currency}
                    value={value}
                    onChange={(v) => onChange(Number(v))}
                    min={0}
                    max={Number.MAX_VALUE}
                    keepWithinRange
                    clampValueOnBlur
                    replaceNanWithZero
                  />
                </FormField>
              )}
            />
          )}
          <Controller
            name="contactPersonName"
            control={control}
            rules={{ required: t('portfolio:required.contactName') }}
            render={({ field: { onChange, value } }) => (
              <FormField
                label={t('portfolio:fields.contactPerson.name.label')}
                id="contactPersonName"
                isRequired
                isInvalid={!!errors.contactPersonName}
                error={errors.contactPersonName?.message}
              >
                <Input
                  width="100%"
                  placeholder="John Doe"
                  {...register('contactPersonName')}
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                />
              </FormField>
            )}
          />

          <Controller
            name="contactPersonEmail"
            control={control}
            rules={{ required: t('portfolio:required.contactEmail') }}
            render={({ field: { onChange, value } }) => (
              <FormField
                label={t('portfolio:fields.contactPerson.email.label')}
                id="contactPersonEmail"
                isRequired
                isInvalid={!!errors.contactPersonEmail}
                error={errors.contactPersonEmail?.message}
              >
                <Input
                  width="100%"
                  placeholder="John.doe@celsia.io"
                  {...register('contactPersonEmail')}
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                />
              </FormField>
            )}
          />

          <Controller
            name="eligibilityStatus"
            control={control}
            rules={{ required: t('portfolio:required.eligibilityStatus') }}
            render={({ field: { value, onChange } }) => (
              <FormField
                label={t('portfolio:fields.eligibilityStatus.label')}
                id="eligibilityStatus"
                isRequired
                isInvalid={!!errors.eligibilityStatus}
                error={errors.eligibilityStatus?.message}
                helpText={t('portfolio:fields.eligibilityStatus.tooltip')}
              >
                <VStack width="100%">
                  <RadioCardGroup
                    options={eligibilityOptions}
                    value={value}
                    onChange={(newStatus) => onChange(newStatus as EligibilityStatus)}
                    cardWidth="455px"
                  />
                </VStack>
              </FormField>
            )}
          />
        </VStack>
      </form>
    </Modal>
  );
};
